import React, { useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { Printer, ArrowLeft, Check, ZoomIn, ZoomOut } from "lucide-react";
import puentesLogo from "../assets/img/puentes-logo.png";
import utnLogo from "../assets/img/utn-logo.png";
import signatureYacoy from "../assets/img/signature_yacoy.png";

const formatDate = () =>
  new Date().toLocaleDateString("es-AR", { day: "numeric", month: "long", year: "numeric" });

/**
 * Renders a custom certificate in A4 preview format with print and optional confirm action.
 *
 * Props:
 *   title        – certificate title (uppercase)
 *   text         – certificate body text
 *   studentName  – full name
 *   studentDni   – DNI number
 *   diplomaName  – diploma name
 *   municipality – municipality (optional)
 *   onClose      – close handler
 *   onBack       – if provided, shows "← Volver a editar" (used in ACO draft step)
 *   onConfirm    – if provided, shows "Confirmar y Emitir" button
 *   confirmLabel – label for confirm button (default "Confirmar y Emitir")
 *   isConfirming – loading state for confirm button
 */
const CustomCertPrintModal = ({
  title,
  text,
  studentName,
  studentDni,
  diplomaName,
  municipality,
  onClose,
  onBack,
  onConfirm,
  confirmLabel = "Confirmar y Emitir",
  isConfirming = false,
}) => {
  const [zoom, setZoom] = useState(0.52);

  return (
    <Modal
      show={true}
      onHide={onClose}
      fullscreen
      backdrop="static"
      className="cert-preview-modal"
    >
      <Modal.Header closeButton className="border-0 bg-dark text-white py-2 px-4">
        <div className="d-flex align-items-center gap-3">
          <span className="fw-bold fs-6">Vista previa — {title || "CONSTANCIA"}</span>
          <div className="d-flex gap-2 ms-3">
            <button
              className="btn btn-sm btn-outline-light rounded-circle p-1"
              onClick={() => setZoom(z => Math.max(0.25, z - 0.1))}
              title="Reducir"
            >
              <ZoomOut size={16} />
            </button>
            <span className="text-white small d-flex align-items-center px-1">{Math.round(zoom * 100)}%</span>
            <button
              className="btn btn-sm btn-outline-light rounded-circle p-1"
              onClick={() => setZoom(z => Math.min(1.0, z + 0.1))}
              title="Ampliar"
            >
              <ZoomIn size={16} />
            </button>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body
        id="cert-preview-body"
        className="d-flex justify-content-center bg-secondary bg-opacity-75 overflow-auto p-4"
      >
        {/* A4 page */}
        <div
          id="certificate-to-print"
          className="bg-white shadow-lg"
          style={{
            width: "210mm",
            minHeight: "297mm",
            fontFamily: "'Times New Roman', Times, serif",
            color: "black",
            transform: `scale(${zoom})`,
            transformOrigin: "top center",
            padding: "25mm",
            boxSizing: "border-box",
            flexShrink: 0,
          }}
        >
          {/* Logos */}
          <div className="d-flex justify-content-between align-items-start mb-5">
            <div className="d-flex flex-column align-items-center" style={{ width: "200px" }}>
              <img src={puentesLogo} alt="Logo Puentes" style={{ width: "130px", marginBottom: "10px" }} />
              <div className="text-center" style={{ fontSize: "11px", fontWeight: "bold", lineHeight: "1.2" }}>
                PUENTES<br />PLAN DE INTEGRACIÓN<br />TERRITORIAL<br />UNIVERSITARIA
              </div>
            </div>
            <div className="d-flex flex-column align-items-center" style={{ width: "220px" }}>
              <img src={utnLogo} alt="Logo UTN" style={{ width: "80px", marginBottom: "10px" }} />
              <div className="text-center" style={{ fontSize: "11px", fontWeight: "bold", lineHeight: "1.2" }}>
                UNIVERSIDAD TECNOLÓGICA NACIONAL<br />DIPLOMATURAS {new Date().getFullYear()}
              </div>
            </div>
          </div>

          {/* Título */}
          <div className="text-center mt-5 mb-4 pt-4">
            <h1 style={{ fontSize: "26px", textDecoration: "underline", fontWeight: "bold" }}>
              {title || "CONSTANCIA"}
            </h1>
          </div>

          {/* Identificación del alumno */}
          <div style={{ fontSize: "18px", lineHeight: "1.6", marginBottom: "20px" }}>
            <p style={{ textAlign: "justify" }}>
              La presente se extiende a nombre de: <strong>{(studentName || "").toUpperCase()}</strong>,
              DNI: <strong>{studentDni}</strong>
              {diplomaName ? (
                <>, alumno/a de la Diplomatura en: <strong>{diplomaName.toUpperCase()}</strong></>
              ) : null}
              {municipality ? <>, del Municipio de <strong>{municipality.toUpperCase()}</strong></> : null}.
            </p>
          </div>

          {/* Texto personalizado */}
          <div style={{ fontSize: "18px", lineHeight: "1.8" }}>
            <div style={{ textAlign: "justify", whiteSpace: "pre-wrap" }}>{text}</div>
            <div style={{ width: "100%", borderBottom: "2px dashed #000", marginTop: "20px" }} />
          </div>

          {/* Fecha */}
          <div className="mt-4 text-end" style={{ fontSize: "18px", paddingRight: "15mm" }}>
            <p>{municipality ? `${municipality.toUpperCase()}, ` : ""}{formatDate()}</p>
          </div>

          {/* Firma */}
          <div
            className="d-flex flex-column align-items-center"
            style={{ position: "absolute", bottom: "60px", left: "0", right: "0" }}
          >
            <div style={{ width: "350px", textAlign: "center" }}>
              <img src={signatureYacoy} alt="Firma" style={{ width: "320px", marginBottom: "-10px" }} />
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0 bg-dark py-2 px-4 d-flex gap-2">
        {onBack && (
          <Button variant="outline-light" className="rounded-pill px-4 d-flex align-items-center gap-2" onClick={onBack}>
            <ArrowLeft size={16} /> Volver a editar
          </Button>
        )}
        <Button
          variant="outline-light"
          className="rounded-pill px-4 d-flex align-items-center gap-2"
          onClick={() => window.print()}
        >
          <Printer size={16} /> Imprimir
        </Button>
        {onConfirm && (
          <Button
            variant="success"
            className="rounded-pill px-4 fw-bold d-flex align-items-center gap-2 shadow-sm ms-auto"
            onClick={onConfirm}
            disabled={isConfirming}
          >
            {isConfirming ? <Spinner animation="border" size="sm" /> : <Check size={16} />}
            {confirmLabel}
          </Button>
        )}
      </Modal.Footer>

      <style>{`
        @page { size: A4; margin: 0 !important; }
        @media print {
          body * { visibility: hidden !important; }
          #certificate-to-print,
          #certificate-to-print * { visibility: visible !important; }
          #certificate-to-print {
            position: fixed !important;
            top: 0 !important; left: 0 !important;
            width: 210mm !important; height: 297mm !important;
            transform: none !important;
            margin: 0 !important; box-shadow: none !important;
            padding: 25mm !important;
          }
        }
      `}</style>
    </Modal>
  );
};

export default CustomCertPrintModal;
